import { useCallback, useEffect, useState } from 'react';
import type { Operaciones } from '../api/operaciones';
import type { ComisionDelPartner, LiquidacionDelPartner, Partner } from '../api/ops/partners';
import { ErrorApi } from '../api/problema';
import type { EntradaMenu, Menu } from '../logica/navegacion';
import type { Vista } from '../marco/PanelNegocio';
import { RUTA_PORTAL_PARTNER } from '../rutas';
import { SECCIONES, type Seccion } from './logica';

/** Datos y menú del portal de partners (1.23), sin nada visual. */
export interface Carga<T> {
    datos: T | null;
    error: unknown;
    recargar: () => void;
}

/** Carga sencilla: vuelve a pedir con `recargar`; un 401 sin renovación acaba la sesión. */
export function useCarga<T>(pedir: (() => Promise<T>) | null, onCaducada: () => void): Carga<T> & { poner: (v: T) => void } {
    const [estado, setEstado] = useState<{ datos: T | null; error: unknown }>({ datos: null, error: null });
    const [vuelta, setVuelta] = useState(0);
    const [pedirFijo] = useState(() => pedir);
    useEffect(() => {
        if (pedirFijo === null) {
            return;
        }
        let vivo = true;
        pedirFijo()
            .then((datos) => {
                if (vivo) {
                    setEstado({ datos, error: null });
                }
            })
            .catch((error: unknown) => {
                if (!vivo) {
                    return;
                }
                if (error instanceof ErrorApi && error.estado === 401) {
                    onCaducada();
                    return;
                }
                setEstado((e) => ({ datos: e.datos, error }));
            });
        return () => {
            vivo = false;
        };
    }, [pedirFijo, vuelta, onCaducada]);
    const recargar = useCallback(() => {
        setVuelta((v) => v + 1);
    }, []);
    const poner = useCallback((datos: T) => {
        setEstado({ datos, error: null });
    }, []);
    return { ...estado, recargar, poner };
}

/**
 * Una petición que depende de una clave (el mes, el negocio…): cada clave, su petición; mientras llega la de la clave
 * nueva no se enseña la de la anterior. `pedir` recibe la clave y se fija la primera vez, como en `useCarga`. Un 401 sin renovación acaba la sesión.
 */
export function usePorClave<T>(clave: string, pedir: (clave: string) => Promise<T>, onCaducada: () => void): { datos: T | null; error: unknown } {
    const [estado, setEstado] = useState<{ clave: string | null; datos: T | null; error: unknown }>({ clave: null, datos: null, error: null });
    const [pedirFijo] = useState(() => pedir);
    useEffect(() => {
        let vivo = true;
        pedirFijo(clave)
            .then((datos) => {
                if (vivo) {
                    setEstado({ clave, datos, error: null });
                }
            })
            .catch((error: unknown) => {
                if (!vivo) {
                    return;
                }
                if (error instanceof ErrorApi && error.estado === 401) {
                    onCaducada();
                    return;
                }
                setEstado({ clave, datos: null, error });
            });
        return () => {
            vivo = false;
        };
    }, [pedirFijo, clave, onCaducada]);
    return estado.clave === clave ? { datos: estado.datos, error: estado.error } : { datos: null, error: null };
}

/** Lo que comparten las secciones del portal. */
export interface DatosPortal {
    ops: Operaciones;
    perfil: Partner;
    pct: string;
    /** Enlace de alta (`referral_url`); nulo mientras la solicitud está en revisión (G1108): sin enlace ni código. */
    enlace: string | null;
    comisiones: Carga<{ data: ComisionDelPartner[]; next_cursor: string | null }>;
    liquidaciones: Carga<{ data: LiquidacionDelPartner[]; next_cursor: string | null }>;
    movil: boolean;
    ruta: string;
    busqueda: URLSearchParams;
    ir: (seccion: Seccion | 'liquidaciones', extra?: string) => void;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    onSesionCaducada: () => void;
    alGuardarPerfil: (p: Partner) => void;
}

export const SIN_CAMPANA: Vista['campana'] = { elementos: [], vacio: '', sinLeer: 0, abrir: () => undefined, leerTodos: () => undefined, tocar: () => undefined, accion: () => undefined, verTodos: null };

function entrada(clave: string, etiqueta: string, icono: string, zona: 'menu' | 'profile'): EntradaMenu {
    return {
        clave,
        modulo: 'planes',
        etiqueta,
        ruta: clave === 'inicio' ? RUTA_PORTAL_PARTNER : `${RUTA_PORTAL_PARTNER}?seccion=${clave}`,
        zona,
        grupo: null,
        icono,
        soloLectura: false,
        contador: null,
        bloque: null,
        noContratada: false,
        enMas: false,
        padre: null,
        anidadaComo: null,
    };
}

/** El menú del portal: fijo en el panel, sin `getMyNavigation` (G1101, ADR-0264), con la ceja «Portal de partners» (prototipo `NAV`); «Mi cuenta» en tu cuenta. */
export function menuDelPortal(): Menu {
    const entradas = SECCIONES.map((s) => entrada(s.clave, s.etiqueta, s.icono, 'menu'));
    return {
        grupos: [{ clave: 'partner', titulo: 'Portal de partners', entradas }],
        entradas,
        quitadas: [],
        anidadas: [],
        perfil: [entrada('cuenta', 'Mi cuenta', 'account', 'profile')],
        barraMovil: entradas.map((e) => e.clave),
        rutaPlan: null,
    };
}

